/**
 * Input and store organization name for current session
 */

import React, { useEffect } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import axios, { AxiosError, AxiosResponse } from 'axios';
import _ from 'lodash';

import { useApp } from '../../_libs/hooks/useApp';
import { useBrand } from '../../_libs/hooks/useBrand';

import Navigation from '../../molecules/Navigation';
import ErrorBoundary from '../../organisms/ErrorBoundary';

const IntroOrganizationName = (): JSX.Element => {
  const {
    reportData,
    userId,
  } = useApp();
  const {
    defaultColors,
    primaryBrand,
    coBrand,
    setPrimaryBrand,
    isValidBrand,
  } = useBrand();
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
  } = useForm();
  const navigate = useNavigate();

  // If no "session" detected, go back to create one
  useEffect(() => { if (reportData === null) { navigate('/'); } }, []);

  function onSubmit (formData: Record<any, any>) {
    const shouldUpdateOrganization = isValidBrand(primaryBrand);
    if (shouldUpdateOrganization) {
      const _primaryBrand = _.cloneDeep(primaryBrand);
      _primaryBrand.name = formData.organizationName || _primaryBrand.name;
      _primaryBrand.placement = 'primary';
      setPrimaryBrand(_primaryBrand);
      axios
        .post('/update_organization/', {
          organizationId: _primaryBrand.id,
          coBrandId: coBrand.id,
          reportId: reportData.id,
          key: 'name',
          value: _primaryBrand.name,
        })
        .then((res: AxiosResponse) => {
          if (res.data.status !== 'OK') { console.warn(res.data.message); }
        })
        .catch((err: AxiosError) => { console.error(err); });
      navigate('/zip/');
    } else {
      axios
        .post('/create_organization/', {
          name: formData.organizationName,
          userId,
          reportId: reportData.id,
        })
        .then((res: AxiosResponse) => {
          const data = res.data;
          if (data.status === 'OK') {
            const org = data.organization;
            setPrimaryBrand({
              id: org.id,
              name: org.name,
              placement: 'primary',
              colors: defaultColors,
            });
            navigate('/zip/');
          } else {
            setError(
              'organizationName',
              {
                type: 'custom',
                message: 'Could not save organization. Try submitting again.',
              }
            );
          }
        });
    }
  }

  return (
    <ErrorBoundary>
      <Navigation />
      <Container>
        <Row align="center" style={{
          minHeight: 'calc(100vh - 105px)',
          paddingBottom: '52.5px',
        }}>
          <Col sm={8}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <label className="block" htmlFor="organizationName">
                <h1 className="f-serif f-normal">
                  Enter Your Organization's Name
                </h1>
                <Row>
                  <Col md={8}>
                    <input
                      className="mb-2 block full-width"
                      defaultValue={primaryBrand?.name}
                      {
                        ...register('organizationName', {
                          required: 'Please enter a name to continue.',
                          pattern: {
                            value: /^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$/g,
                            message: 'Only letters, spaces, and some punctuation are accepted.',
                          },
                        })
                      }
                    />
                    { errors.organizationName && <div>{errors.organizationName.message.toString()}</div> }
                  </Col>
                </Row>
              </label>
              <button type="submit">Next</button>
            </form>
          </Col>
        </Row>
      </Container>
    </ErrorBoundary>
  );
};

export default IntroOrganizationName;
